import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  Link,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Person,
  Phone,
  Lock,
} from '@mui/icons-material';

import { createTheme, ThemeProvider } from "@mui/material/styles"; // ⭐ ADDED

import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import logo from '../../logo/logo.gif';
import enhancedAuthService, { SignUpRequest } from '../../services/enhancedAuth';

// ⭐ ADDED — EV THEME (for fixing textfields, buttons, alerts)
const evTheme = createTheme({
  palette: {
    primary: { main: "#00FF66" },
    text: { primary: "#E5F0FF", secondary: "#C8D4EA" },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            color: "#E5F0FF",
            "& fieldset": { borderColor: "rgba(255,255,255,0.4)" },
            "&:hover fieldset": { borderColor: "#00FF66" },
            "&.Mui-focused fieldset": { borderColor: "#00FF66" },
          },
          "& .MuiInputLabel-root": { color: "#C8D4EA" },
          "& .MuiInputLabel-root.Mui-focused": { color: "#00FF66" },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontWeight: 600,
          textTransform: "none",
        },
        containedPrimary: {
          backgroundColor: "#00FF66",
          "&:hover": { backgroundColor: "#00cc52" },
        },
      },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: 10 } },
    },
  },
});

const schema = yup.object({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  phone: yup.string().matches(/^\+?[\d\s-()]+$/, 'Invalid phone number'),
  password: yup
    .string()
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%?&])[A-Za-z\d@$!%?&]/,
      'Password must contain uppercase, lowercase, number & special character'
    )
    .required('Password is required'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('Confirm password is required'),
});

const SignUpForm: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [pendingVerification, setPendingVerification] = useState(false);
  const [identifier, setIdentifier] = useState('');
  const [otp, setOtp] = useState('');

  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Omit<SignUpRequest, 'acceptTerms'>>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (data: Omit<SignUpRequest, 'acceptTerms'>)  => {
    setIsLoading(true);
    setMessage(null);

    try {
      const response = await enhancedAuthService.signUp({ ...data, acceptTerms: true });

      if (response.success) {
        if (response.data?.requiresVerification) {
          setPendingVerification(true);
          setIdentifier(data.email || data.phone || '');
          setMessage({
            type: 'success',
            text:
              response.message ||
              'Account created. Enter OTP sent to your email/phone.',
          });
          return;
        }

        setMessage({
          type: 'success',
          text: response.message || 'Account created successfully!',
        });
        setTimeout(() => navigate('/login'), 3000);
      } else {
        setMessage({
          type: 'error',
          text: response.error || 'Sign up failed. Try again.',
        });
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text:
          error.response?.data?.error ||
          error.response?.data?.message ||
          'Sign up failed',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!identifier || !otp.trim()) {
      setMessage({ type: 'error', text: 'Enter OTP and identifier' });
      return;
    }

    setIsVerifying(true);
    setMessage(null);

    try {
      const resp = await enhancedAuthService.verifyUserOtp({ identifier, otp });
      if (resp.success) {
        setMessage({
          type: 'success',
          text: resp.message || 'Verification successful!',
        });
        setPendingVerification(false);
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setMessage({ type: 'error', text: resp.error || 'Verification failed' });
      }
    } catch (err: any) {
      setMessage({
        type: 'error',
        text: err?.response?.data?.error || err?.message || 'Verification failed',
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    if (!identifier) {
      setMessage({ type: 'error', text: 'Missing identifier' });
      return;
    }
    setIsResending(true);
    setMessage(null);

    try {
      const resp = await enhancedAuthService.resendOtp({ identifier });
      if (resp.success) {
        setMessage({ type: 'success', text: resp.message || 'OTP resent' });
      } else {
        setMessage({ type: 'error', text: resp.error || 'Failed to resend OTP' });
      }
    } catch (err: any) {
      setMessage({
        type: 'error',
        text: err?.response?.data?.error || err?.message || 'Failed to resend OTP',
      });
    } finally {
      setIsResending(false);
    }
  };

  return (
    <ThemeProvider theme={evTheme}> {/* ⭐ WRAPPED RETURN WITH THEME */}
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        {/* LEFT PANEL */}
        <Box
                sx={{
                  width: { xs: "0%", md: "50%" },
                  background: "linear-gradient(180deg, #0A1A36, #112B54)",
                  borderRight: "1px solid #1e3c72",
                  color: "white",
                  p: 6,
                  display: { xs: "none", md: "flex" },
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <img
                  src={logo}
                  style={{
                    width: "250px",
                    height: "auto",
                    objectFit: "contain",
                    borderRadius: "10px",
                  }}
                  alt="EV91 Logo"
                />
        
                <Typography
                          variant="h3"
                          sx={{
                            fontWeight: 800,
                            lineHeight: 1.2,
                            marginTop: 4,
                            fontSize: "3.5rem",
                            color: "#b2d2d8ff",
                            textShadow: "0px 3px 12px rgba(0,0,0,0.4)",
                          }}
                        >
                          Drive The Future <br />
                          With
                          <span
                            style={{
                              color: "#4EF06B",
                              fontWeight: 900,
                              marginLeft: 10,
                            }}
                          >
                            Voice⚡
                          </span>
                        </Typography>
        
                <Typography
                  sx={{
                    color: "#AFC7E4",
                    mt: 2,
                    fontSize: "1.2rem",
                    maxWidth: "400px",
                  }}
                >
                  Welcome to the EV91Technologies Dashboard — manage vehicles, riders &
                  operations with ease.
                </Typography>
              </Box>

        {/* RIGHT PANEL */}
        <Box
          sx={{
            flex: 1,
            backgroundColor: '#b2d2d8ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 6,
          }}
        >
          <Paper
            elevation={6}
            sx={{
              width: '100%',
              background: 'linear-gradient(180deg, #0A1A36, #112B54)',
              maxWidth: 555,
              borderRadius: 5,
              px: 4,
              py: 5,
              boxShadow: '#112B54',
            transition: "all 0.28s ease",
            "&:hover": {
             transform: "scale(1.01)",
              boxShadow: "0 16px 45px rgba(0,0,0,0.6)",
            },
              
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontWeight: 700, color: '#00FF66', mb: 1 }}
              >
                Create Account
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sign up to get started with your EV91 dashboard 🚀
              </Typography>
            </Box>

            {message && (
              <Alert severity={message.type} sx={{ mb: 3 }}>
                {message.text}
              </Alert>
            )}

            {/* FORM START */}
            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="First Name"
                      error={!!errors.firstName}
                      helperText={errors.firstName?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Person sx={{ color: "#4EF06B" }}/>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />

                <Controller
                  name="lastName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Last Name"
                      error={!!errors.lastName}
                      helperText={errors.lastName?.message}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Person sx={{ color: "#4EF06B" }}/>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Box>

              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Email Address"
                    type="email"
                    margin="normal"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email sx={{ color: "#4EF06B" }}/>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Phone Number"
                    margin="normal"
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone sx={{ color: "#4EF06B" }}/>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    margin="normal"
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock sx={{ color: "#4EF06B" }}/>
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)}>
                            {showPassword ? <VisibilityOff sx={{ color: "#4EF06B" }}/> : <Visibility sx={{ color: "#4EF06B" }}/>}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              <Controller
                name="confirmPassword"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Confirm Password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    margin="normal"
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock sx={{ color: "#4EF06B" }}/>
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          >
                            {showConfirmPassword ? <VisibilityOff sx={{ color: "#4EF06B" }}/> : <Visibility sx={{ color: "#4EF06B" }}/>}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              {/* OTP PANEL */}
              {pendingVerification ? (
                <>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Enter OTP sent to <strong>{identifier}</strong>
                  </Typography>

                  <TextField
                    label="OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    fullWidth
                    margin="normal"
                  />

                  <Button
                    fullWidth
                    variant="contained"
                    disabled={isVerifying}
                    sx={{ mt: 2, mb: 1, py: 1.5 }}
                    onClick={handleVerifyOtp}
                    startIcon={isVerifying ? <CircularProgress size={20} /> : null}
                  >
                    {isVerifying ? 'Verifying...' : 'Verify OTP'}
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    disabled={isResending}
                    sx={{ mt: 1, mb: 2 }}
                    onClick={handleResendOtp}
                    startIcon={isResending ? <CircularProgress size={16} /> : null}
                  >
                    {isResending ? 'Resending...' : 'Resend OTP'}
                  </Button>

                  <Box sx={{ textAlign: 'center' }}>
                    <Button variant="text" onClick={() => setPendingVerification(false)}>
                      Cancel
                    </Button>
                  </Box>
                </>
              ) : (
                <>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={isLoading}
                    sx={{ mt: 3, mb: 2, py: 1.5 }}
                    startIcon={isLoading ? <CircularProgress size={20} /> : null}
                  >
                    {isLoading ? 'Creating Account...' : 'Sign Up'}
                  </Button>

                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2">
                      Already have an account?{' '}
                      <Link component={RouterLink} to="/login">
                        Sign In
                      </Link>
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default SignUpForm;
